/**
 * projectBookingApi — Project Board Phase 2: mint a context-carrying booking
 * link for a project's scheduling todo. Standalone module on the shared
 * apiClient (frozen api.ts untouched). The Send-Invite action calls mintInvite,
 * then drops the returned button_html into an email DRAFT (never a send).
 */
import { api } from './apiClient';

export interface MintedInvite {
  token: string;
  booking_url: string;
  button_html: string;
  event_type: { id: number; slug: string; name: string; duration_minutes: number };
}

export interface TodoEmailDraft {
  to: string[];
  subject: string;
  body_html: string;
  /** null = this todo is not a template action (open a plain composer). */
  action: 'scheduler' | 'workflow_approval' | null;
}

/** One live booking link — a person × a call (`GET /scheduling/call-links`). */
export interface CallLink {
  event_type_id: number;
  slug: string;
  name: string;
  duration_minutes: number;
  timezone: string | null;
  location_type: string | null;
  status: string;
  host_user_id: number;
  host_name: string | null;
  /** The call type this link is an instance of (null = a personal event type). */
  event_template_id: number | null;
  /** That call type's key (`assessment-call`, `kickoff-call`), so a surface can offer one call's links. */
  template_key: string | null;
  kind: string | null;
  floor_field: string | null;
}

export interface TodoBookingContact { id: number; first_name: string | null; last_name: string | null; email: string | null; phone: string | null }

/** What the in-app booker needs to book a scheduling to-do live. */
export interface TodoBookingInvite {
  token: string;
  label: string;
  event_type: { id: number; slug: string; name: string; duration_minutes: number; timezone: string | null; location_type: string | null; location_value: string | null };
  host: { id: number | null; name: string | null };
  project_id: number | null;
  client_card_id: string | null;
  contacts: TodoBookingContact[];
}

export const projectBookingApi = {
  /** Every live booking link, one row per person × call. */
  callLinks: (): Promise<CallLink[]> => api.get('/scheduling/call-links').then((r) => r.data.data),

  /** Mint the invite for a scheduling to-do (binding `eventTypeId` first when given) so the booker can book it live. */
  inviteForTodo: (todoId: number, eventTypeId?: number | null): Promise<TodoBookingInvite> =>
    api.post(`/scheduling/project-invites/todo/${todoId}`, eventTypeId != null ? { event_type_id: eventTypeId } : {}).then((r) => r.data.data),

  mintInvite: (input: { todo_id?: number; project_id?: number; client_card_id?: string; call_type?: string }): Promise<MintedInvite> =>
    api.post('/scheduling/project-invites', input).then((r) => r.data.data),

  // Template-composer model: the resolved email draft for a project todo
  // (scheduler / workflow-approval) — an editable template with the booking /
  // share link + client tokens resolved server-side, for the composer preload.
  emailDraft: (todoId: number): Promise<TodoEmailDraft> =>
    api.get(`/projects/todos/${todoId}/email-draft`).then((r) => r.data.data),
};
